/* eslint-disable vue/sort-keys */
import { defineStore } from "pinia";
import { router } from "@router/router";
import { watch } from "vue";
import { Api } from "@helpers/api";

export const useEditorStore = defineStore("editor", {
    state: () => ({
        // Interne dingetjes
        swal: null,
        i18n: null,

        // Dynamisch in te stellen vanuit initialize()
        url: "",
        name: "",

        // 'Props' vervangers die je vanuit de component kunt zetten
        preLoaded: null,
        idOverride: null,

        // Interne state
        apiArgs: null,
        id_param: "id",
        errors: {},
        model: {},
        modelOriginal: null,
        validFields: {},
        formSchema: {
            triggers: [],
            fields: [],
        },
        preventRouterReplace: false,
        overrideNewSavedRoute: null,
        unwatchModel: null, // Slaat de unwatch-functie op voor de cleanup
    }),
    getters: {
        hasChanges() {
            return JSON.stringify(this.model) !== JSON.stringify(this.modelOriginal);
        },
        isNew() {
            return this.id === "new";
        },
        id() {
            return this.idOverride ?? router.currentRoute.value.params[this.id_param];
        },
        title() {
            if (!this.name) return null;

            return this.isNew
                ? this.i18n.global.t(`model.${this.name}.new`)
                : this.i18n.global.t(`model.${this.name}.edit`);
        },
        modelDeleteConfirmation() {
            return {
                cancelButtonText: "Annuleren",
                confirmButtonText: "Verwijderen",
                icon: "warning",
                reverseButtons: true,
                showCancelButton: true,
                title: "Weet je het zeker?",
                text: "Dit kan niet ongedaan worden gemaakt.",
            };
        },
        modelDeleteSuccess() {
            return {
                icon: "success",
                showConfirmButton: false,
                text: "Succesvol verwijderd",
                timer: 1250,
                title: "Verwijderd!",
            };
        },
        modelDeleteFailed() {
            return {
                icon: "error",
                showConfirmButton: false,
                text: "Er is iets misgegaan",
                timer: 1250,
                title: "Fout!",
            };
        },
        overrideNewSavedRouteParams() {
            return { id: this.model.id };
        },
    },
    actions: {
        initialize(name, url, overrideNewSavedRoute = null) {
            this.name = name;
            this.url = url;
            this.overrideNewSavedRoute = overrideNewSavedRoute;

            // Load schema
            this.loadSchema();

            // Add a watcher for the trigger fields (for dynamic fields)
            this.unwatchModel = watch(
                () => {
                    if (!this.formSchema || !Array.isArray(this.formSchema.triggers)) return [];
                    return this.formSchema.triggers.map((field) => this.model[field]);
                },
                (newTriggerValues, oldTriggerValues) => {
                    if (!oldTriggerValues || oldTriggerValues.length === 0) return;

                    const triggerFired = newTriggerValues.some((value, index) => {
                        return value !== oldTriggerValues[index];
                    });

                    // Change of relevant field = refetch schema
                    if (triggerFired) {
                        this.loadSchema();
                    }
                },
            );

            if (!this.isNew) {
                if (this.preLoaded) {
                    this.model = this.preLoaded;
                    this.modelOriginal = { ...this.preLoaded };
                } else {
                    Api.get(this.url + this.id).then((response) => {
                        if (!response.data.success) {
                            return router.back();
                        }

                        this.model = response.data.data;
                        this.modelOriginal = { ...response.data.data };

                        // Reload schema to adjust for fetched data (dynamic fields)
                        this.loadSchema();
                    });
                }
            }

            const currentRoute = router.currentRoute.value;
            if (currentRoute.query) {
                Object.keys(currentRoute.query).forEach((key) => {
                    this.model[key] = currentRoute.query[key];
                });
            }
        },
        loadSchema() {
            if (!this.url) return;

            const baseSlug = this.url.endsWith("/") ? this.url.slice(0, -1) : this.url;
            const params = new URLSearchParams();

            // Add id
            params.append("id", this.id);

            if (this.formSchema && Array.isArray(this.formSchema.triggers)) {
                this.formSchema.triggers.forEach((triggerField) => {
                    if (
                        this.model[triggerField] !== undefined &&
                        this.model[triggerField] !== null
                    ) {
                        params.append(triggerField, this.model[triggerField]);
                    }
                });
            }

            const schemaUrl = `${baseSlug}/meta/form-schema?${params.toString()}`;

            Api.get(schemaUrl).then((response) => {
                if (response && response.data.data && response.data.success) {
                    this.formSchema = response.data.data;

                    // Vul het model vast met lege/standaardwaardes voor nieuwe velden
                    if (
                        this.formSchema &&
                        this.formSchema.fields &&
                        typeof this.formSchema.fields === "object"
                    ) {
                        Object.values(this.formSchema.fields).forEach((field) => {
                            if (this.model[field.name] === undefined) {
                                this.model[field.name] = field.type === "boolean" ? false : "";
                            }

                            if (this.isNew) {
                                if (field.default !== undefined) {
                                    this.model[field.name] = field.default;
                                }
                            }
                        });
                    }
                }
            });
        },
        setup(swal, i18n) {
            this.swal = swal;
            this.i18n = i18n;
        },
        cleanup() {
            // Stop watching for changes in the model
            if (typeof this.unwatchModel === "function") {
                this.unwatchModel();
            }

            this.url = "";
            this.name = "";
            this.preLoaded = null;
            this.idOverride = null;
            this.id_param = "id";
            this.errors = {};
            this.model = {};
            this.modelOriginal = null;
            this.validFields = {};
            this.formSchema = { triggers: [], fields: [] };
            this.preventRouterReplace = false;
            this.overrideNewSavedRoute = null;
            this.unwatchModel = null;
        },
        afterSuccessfulSave(wasNew = false, goBack = false) {
            if (wasNew) {
                if (this.overrideNewSavedRoute) {
                    // If the override is a string, put it in an array to handle it uniformly
                    const routes = Array.isArray(this.overrideNewSavedRoute)
                        ? this.overrideNewSavedRoute
                        : [
                              {
                                  name: this.overrideNewSavedRoute,
                                  params: this.overrideNewSavedRouteParams,
                              },
                          ];

                    // Replace the current route with the first route, then push the remaining routes.
                    // Await each navigation so the replacement is completed before adding history entries.
                    routes.reduce((navigation, route, index) => {
                        return navigation.then(() => {
                            if (route.params && typeof route.params === "function") {
                                route.params = route.params(this);
                            }

                            if (index === 0) {
                                return router.replace(route);
                            } else {
                                return router.push(route);
                            }
                        });
                    }, Promise.resolve());

                    return;
                }

                if (!this.preventRouterReplace) {
                    router.replace({
                        name: router.currentRoute.value.name,
                        params: { id: this.model.id },
                    });
                }
            }

            if (goBack) {
                router.back();
            }
        },
        back() {
            if (!this.hasChanges) {
                router.back();
                return;
            }

            this.swal({
                cancelButtonText: "Annuleren",
                confirmButtonText: "Opslaan",
                icon: "warning",
                reverseButtons: true,
                showCancelButton: true,
                text: "Wil je de wijzigingen opslaan?",
                title: "Onpgeslagen wijzigingen",
            }).then((result) => {
                if (result.isConfirmed) {
                    this.save(true);
                } else if (result.dismiss === this.swal.DismissReason.cancel) {
                    this.swal({
                        icon: "error",
                        showConfirmButton: false,
                        text: "Wijzigingen geannuleerd",
                        timer: 1250,
                        title: "Geannuleerd",
                    });
                    router.back();
                }
            });
        },
        deleteModel() {
            this.swal(this.modelDeleteConfirmation).then((result) => {
                if (result.isConfirmed) {
                    Api.delete(this.url + this.id).then((response) => {
                        if (response.data.success) {
                            this.swal(this.modelDeleteSuccess);
                            return router.back();
                        }

                        this.swal(this.modelDeleteFailed);
                    });
                }
            });
        },
        getModelForSave() {
            return this.model;
        },
        save(goBack = false) {
            if (this.isNew) {
                Api.post(this.url, this.getModelForSave(), this.apiArgs).then((response) => {
                    if (!response.data.success) {
                        this.errors = response.data.data
                            ? response.data.data
                            : response.data.errors;
                    } else {
                        this.model = response.data.data;
                        this.modelOriginal = { ...response.data.data };
                        this.errors = {};

                        this.afterSuccessfulSave(true, goBack);
                    }
                });
            } else {
                Api.put(this.url + this.id, this.getModelForSave()).then((response) => {
                    if (!response.data.success) {
                        this.errors = response.data.data
                            ? response.data.data
                            : response.data.errors;
                    } else {
                        this.model = response.data.data;
                        this.modelOriginal = { ...response.data.data };
                        this.errors = {};

                        this.afterSuccessfulSave(false, goBack);
                    }
                });
            }
        },
        submit() {
            this.save(true);
        },
        validateField(fieldName) {
            const resourceUrl = this.isNew ? this.url : `${this.url}${this.id}`;

            Api.validate(resourceUrl, this.getModelForSave(), this.isNew, fieldName).then(
                (response) => {
                    if (!response) return;

                    if (response.data && response.data.data) {
                        this.errors = response.data.data;
                        this.validFields = {};

                        Object.keys(this.getModelForSave()).forEach((key) => {
                            const value = this.model[key];
                            if (value !== "" && value !== null && !this.errors[key]) {
                                this.validFields[key] = true;
                            }
                        });
                    } else if (response.data && response.data.valid) {
                        this.errors = {};
                        this.validFields = {};

                        Object.keys(this.getModelForSave()).forEach((key) => {
                            const value = this.model[key];
                            if (value !== "" && value !== null) {
                                this.validFields[key] = true;
                            }
                        });
                    } else {
                        this.errors = {};
                    }
                },
            );
        },
        getDynamicModelContext(field) {
            const chosenType = this.model[field.depends_on];

            if (!chosenType) return "";

            const parts = chosenType.split("\\");
            return parts[parts.length - 1].toLowerCase();
        },
    },
});
